import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { Phone, Clock, Award, ShieldCheck, Wrench, MapPin, Star, Droplets, Thermometer, AlertTriangle, Truck } from "lucide-react";
import { BUSINESS } from "@/lib/business";
import { SERVICES } from "@/lib/services";
import { CITIES } from "@/lib/cities";
import { POSTS } from "@/lib/blog";
import { PhoneButton } from "@/components/site/PhoneButton";
import { QuoteForm } from "@/components/site/QuoteForm";
import { TrustBar } from "@/components/site/TrustBar";
import { Testimonials } from "@/components/site/Testimonials";
import { FAQ } from "@/components/site/FAQ";
import { CTABand } from "@/components/site/CTABand";
import { PreferredSourceButton } from "@/components/site/PreferredSourceButton";

import { faqSchema } from "@/components/site/SchemaJsonLd";
import { HOME_GRAPH } from "@/lib/home-graph";
import homeHero from "@/assets/home-hero.webp";
import homeWarranty from "@/assets/home-warranty.webp";
import homeFleet from "@/assets/home-fleet.webp";
import homeProcess from "@/assets/home-process.webp";
import homeMap from "@/assets/home-map.webp";
import homeReviews from "@/assets/home-reviews.webp";
import homeFleet1024 from "@/assets/home-fleet-1024.webp";
import homeFleet640 from "@/assets/home-fleet-640.webp";
import homeMap1024 from "@/assets/home-map-1024.webp";
import homeMap640 from "@/assets/home-map-640.webp";
import homeProcess1024 from "@/assets/home-process-1024.webp";
import homeProcess640 from "@/assets/home-process-640.webp";
import homeReviews1024 from "@/assets/home-reviews-1024.webp";
import homeReviews640 from "@/assets/home-reviews-640.webp";
import homeWarranty1024 from "@/assets/home-warranty-1024.webp";
import homeWarranty640 from "@/assets/home-warranty-640.webp";


const HOME_FAQS = [
  { q: "What plumbing services does Phoenix Plumbing Co. offer?", a: "We help with water heaters, drain cleaning, leak detection, sewer line repair, toilet and faucet installation, garbage disposals, gas lines, water filtration, kitchen and bathroom plumbing, backflow prevention, new construction plumbing, and emergency plumbing service." },
  { q: "What are your hours?", a: "Phoenix Plumbing Co. offers regular service Monday–Friday from 6 AM–5 PM and Saturday from 6 AM–2 PM. Emergency plumbing calls may be accepted by phone outside regular hours." },
  { q: "Do you offer emergency plumbing service?", a: "Yes — emergency plumbing calls may be accepted by phone outside our regular hours. Call 602-834-1208 and we'll help if we can." },
  { q: "Do you give free estimates?", a: "Installations and replacements are quoted free. Repairs carry a small diagnostic fee that is credited toward the work if you approve the repair." },
  { q: "Are you licensed, bonded, and insured?", a: "Yes — AZ ROC Licensed, Bonded & Insured. Licensed plumbers lead our plumbing work, with apprentices and helpers working under licensed supervision when needed." },
  { q: "Do you offer a warranty?", a: "Every repair and installation is covered by our written workmanship warranty." },
  { q: "What areas do you serve?", a: "Phoenix and nearby Valley cities including Glendale, Peoria, Scottsdale, Tempe, Mesa, Chandler, Gilbert, Surprise, and Goodyear." },
  { q: "How long have you been in business?", a: `Phoenix Plumbing Co. has been family-owned and operated since ${BUSINESS.founded}.` },
];

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Phoenix Plumbing CO | Plumbers in Phoenix AZ" },
      { name: "description", content: "Family-owned Phoenix plumbing company since 2012. Repairs, water heaters, drains & emergency plumbing service across the Valley. Call 602-834-1208." },
      { property: "og:title", content: "Phoenix Plumbing CO | Plumbers in Phoenix AZ" },
      { property: "og:description", content: "Family-owned Phoenix plumbing company since 2012. Repairs, water heaters, drains & emergency plumbing service across the Valley. Call 602-834-1208." },
      { property: "og:url", content: "https://www.phoenixplumbing.co/" },
      { name: "twitter:title", content: "Phoenix Plumbing CO | Plumbers in Phoenix AZ" },
      { name: "twitter:description", content: "Family-owned Phoenix plumbing company since 2012. Repairs, water heaters, drains & emergency plumbing service across the Valley. Call 602-834-1208." },
    ],
    links: [
      { rel: "canonical", href: "https://www.phoenixplumbing.co/" },
      { rel: "preload", as: "image", href: homeHero, fetchpriority: "high" } as any,
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(faqSchema(HOME_FAQS)) },
      { type: "application/ld+json", children: JSON.stringify(HOME_GRAPH) },
    ],
  }),
  component: Index,
});

function Index() {
  return (
    <>
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-secondary via-secondary to-[#0f1a2c] text-secondary-foreground overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-90 bg-cover bg-[center_top_25%]"
          style={{ backgroundImage: `url(${homeHero})` }}
        />
        <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-secondary/95 via-secondary/70 to-secondary/10" />
        <div className="absolute inset-0 opacity-10 pointer-events-none" aria-hidden>
          <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-primary blur-3xl" />
          <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-accent blur-3xl" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 py-14 md:py-24">
          <div className="max-w-2xl">
            <p className="inline-block rounded-full bg-accent/20 text-accent px-3 py-1 text-xs font-bold uppercase tracking-widest">
              Family-Owned • Phoenix, AZ • Since {BUSINESS.founded}
            </p>
            <h1 className="mt-4 text-4xl md:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Phoenix Plumbing Co. — Plumbing Repairs, Water Heaters, Drains{" "}
              <span className="block text-accent mt-2">& Emergency Service</span>
            </h1>
            <h2 className="mt-4 text-lg md:text-xl font-bold text-accent">
              Licensed, Bonded &amp; Insured Phoenix Plumbing Contractors
            </h2>
            <p className="mt-4 text-lg text-secondary-foreground/90">
              Water heaters, drains, leaks, sewer lines, fixtures, and gas lines — done right by a
              local family-owned crew. Call {BUSINESS.phone}.
            </p>
            <ul className="mt-5 flex flex-wrap gap-2">
              {[
                "Trusted by thousands of Arizona homeowners since 2012",
                "Emergency Calls by Phone",
                "Upfront Quotes",
                "Written Warranty",
              ].map((b) => (
                <li
                  key={b}
                  className="rounded-full border border-accent/40 bg-accent/10 px-3 py-1.5 text-xs md:text-sm font-bold text-accent"
                >
                  {b}
                </li>
              ))}
            </ul>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <PhoneButton size="lg" />
              <a href="#quote" className="inline-flex items-center justify-center rounded-md border-2 border-accent px-7 py-4 text-lg font-extrabold text-accent hover:bg-accent hover:text-accent-foreground transition-colors">
                Get Free Quote
              </a>
            </div>
            <div className="mt-8"><TrustBar dark /></div>
          </div>
        </div>
      </section>

      {/* Quote form — directly after the hero */}
      <section id="quote" className="bg-muted/40 border-b border-border">
        <div className="mx-auto max-w-3xl px-4 py-12">
          <h2 className="text-3xl md:text-4xl font-extrabold text-secondary text-center">
            Get a Free Quote in 60 Seconds
          </h2>
          <div className="mt-6"><QuoteForm /></div>
        </div>
      </section>

      {/* Google Preferred Sources */}
      <section className="mx-auto max-w-3xl px-4 pt-10">
        <PreferredSourceButton />
      </section>



      {/* Benefits 4-up */}
      <section className="mx-auto max-w-7xl px-4 py-14">
        <div className="grid gap-5 md:grid-cols-4">
          {[
            { Icon: Phone, t: "Free Quotes", d: "On installs & replacements." },
            { Icon: Award, t: "Upfront Pricing", d: "Flat price before any work." },
            { Icon: Clock, t: "Emergency Service", d: "Emergency plumbing calls accepted by phone." },
            { Icon: ShieldCheck, t: "Licensed & Insured", d: "AZ ROC Licensed, Bonded & Insured." },
          ].map(({ Icon, t, d }) => (
            <div key={t} className="rounded-xl border border-border bg-card p-5 text-center">
              <Icon className="mx-auto h-8 w-8 text-primary" />
              <div className="mt-3 font-extrabold text-secondary">{t}</div>
              <div className="text-sm text-muted-foreground mt-1">{d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Services grid */}
      <section id="services" className="bg-muted/40 border-y border-border scroll-mt-20">
        <div className="mx-auto max-w-7xl px-4 py-14">
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-secondary">
            Plumbing Services in Phoenix
          </h2>
          <p className="mt-2 text-muted-foreground">From a dripping faucet to a full repipe — we do it all.</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s) => (
              <Link key={s.slug} to={`/${s.slug}` as any} className="group rounded-xl border border-border bg-card overflow-hidden hover:border-primary transition-colors">
                <div className="h-28 bg-gradient-to-br from-secondary via-secondary to-[#0f1a2c] flex items-center justify-center">
                  <Wrench className="h-10 w-10 text-accent" />
                </div>
                <div className="p-5">
                  <Wrench className="h-6 w-6 text-primary" />
                  <div className="mt-3 font-extrabold text-secondary">{s.name}</div>
                  <p className="mt-1 text-sm text-muted-foreground">{s.short}</p>
                  <span className="mt-3 inline-block text-sm font-bold text-primary group-hover:underline">Learn more →</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Phoenix plumbing repairs */}
      <section className="mx-auto max-w-4xl px-4 py-14">
        <h2 className="text-3xl md:text-4xl font-extrabold text-secondary">
          Phoenix Plumbing Repairs, Done Right the First Time
        </h2>
        <p className="mt-5 text-lg text-foreground leading-relaxed">
          Most of our calls start the same way: something that was working yesterday isn't working
          today. A water heater that quit overnight, a toilet that won't stop running, a faucet
          dripping behind the wall. Our Phoenix plumbing repair techs arrive in fully stocked trucks,
          diagnose the actual cause instead of patching the symptom, and quote a flat price before any
          work begins. Whether it's a five-minute fix or a full repipe, every repair is backed by a
          written workmanship warranty from a licensed, bonded and insured Arizona contractor.
        </p>
        <p className="mt-4 text-lg text-foreground leading-relaxed">
          Common repairs we handle same-day:{" "}
          <Link to="/water-heater-installation-and-repair" className="text-primary font-semibold underline">water heater repair</Link>,{" "}
          <Link to="/toilet-installation-and-repair-phoenix-az" className="text-primary font-semibold underline">toilet and faucet repair</Link>,{" "}
          <Link to="/drain-cleaning-and-clog-removal" className="text-primary font-semibold underline">drain cleaning and clog removal</Link>,{" "}
          <Link to="/leak-detection-and-repair" className="text-primary font-semibold underline">leak detection</Link>,{" "}
          <Link to="/slab-leak-detection-and-repair-phoenix-az" className="text-primary font-semibold underline">slab leak repair</Link>, and{" "}
          <Link to="/pipe-repair-and-replacement-phoenix-az" className="text-primary font-semibold underline">pipe repair and replacement</Link>.
        </p>
      </section>

      {/* Local plumbing company */}
      <section className="bg-muted/40 border-y border-border">
        <div className="mx-auto max-w-4xl px-4 py-14">
          <h2 className="text-3xl md:text-4xl font-extrabold text-secondary">
            A Local Plumbing Company You Can Actually Reach
          </h2>
          <p className="mt-5 text-lg text-foreground leading-relaxed">
            Phoenix Plumbing Co. is a local plumbing company, not a national call center. When you call{" "}
            {BUSINESS.phone}, you reach our shop off Tatum Boulevard in North Phoenix, and the plumber
            who shows up is one of ours — background-checked, licensed, and local. Three generations of
            our family have worked the Valley's plumbing, from 1960s slab-on-grade homes in central
            Phoenix to new builds in Anthem and Goodyear. That's why local plumbers matter here:
            Phoenix's hard water, slab construction, and monsoon sewer backups aren't problems you learn
            from a manual.
          </p>
        </div>
      </section>

      {/* Local plumbing help — anchor copy block */}
      <section className="mx-auto max-w-4xl px-4 py-14">
        <h2 className="text-3xl md:text-4xl font-extrabold text-secondary">
          Local Plumbing Help Without the Runaround
        </h2>
        <p className="mt-5 text-lg text-foreground leading-relaxed">
          When something goes wrong with your plumbing, you need clear answers fast. Phoenix
          Plumbing Co. helps with common plumbing repairs, urgent leaks, clogged drains, broken
          water heaters, sewer line issues, fixture problems, gas line work, and plumbing upgrades.
        </p>
        <p className="mt-4 text-lg text-foreground leading-relaxed">
          Our goal is simple: find the problem, explain your options, and complete the work the
          right way. We serve Phoenix and nearby areas with straightforward pricing, clean work,
          and helpful communication from start to finish.
        </p>
        <p className="mt-4 text-foreground leading-relaxed">
          Get help with{" "}
          <Link to="/emergency-plumbing-phoenix" className="text-primary font-semibold underline">emergency plumbing in Phoenix</Link>,{" "}
          <Link to="/water-heater-installation-and-repair" className="text-primary font-semibold underline">water heater repair</Link>,{" "}
          <Link to="/drain-cleaning-and-clog-removal" className="text-primary font-semibold underline">drain cleaning</Link>,{" "}
          <Link to="/leak-detection-and-repair" className="text-primary font-semibold underline">leak detection</Link>,{" "}
          <Link to="/sewer-line-repair-and-replacement-phoenix-az" className="text-primary font-semibold underline">sewer line repair</Link>,{" "}
          <Link to="/gas-line-installation-and-repair-phoenix-az" className="text-primary font-semibold underline">gas line plumbing</Link>, or{" "}
          <Link to="/contact" className="text-primary font-semibold underline">contact Phoenix Plumbing Co.</Link>
        </p>
      </section>

      {/* Why us */}
      <section className="mx-auto max-w-7xl px-4 py-14 grid gap-10 lg:grid-cols-2 items-center">
        <div className="grid gap-5 sm:grid-cols-2">
          {[
            { t: "Upfront, flat-rate pricing", d: "You see the price before we lift a wrench. No hourly surprises." },
            { t: `Family-owned since ${BUSINESS.founded}`, d: "Local Phoenix plumbing company, same family from the start." },
            { t: "Licensed plumbers lead our work", d: "Licensed plumbers lead our plumbing work. Apprentices and helpers work under licensed supervision when needed." },
            { t: "Written workmanship warranty", d: "Every repair and install is backed by our written workmanship warranty." },
          ].map((u) => (
            <div key={u.t} className="rounded-xl border border-border bg-card p-5">
              <div className="font-extrabold text-secondary">{u.t}</div>
              <p className="mt-1 text-sm text-muted-foreground">{u.d}</p>
            </div>
          ))}
        </div>
        <div>
          <div className="rounded-2xl overflow-hidden shadow-xl ring-1 ring-border">
            <img srcSet={`${homeWarranty640} 640w, ${homeWarranty1024} 1024w, ${homeWarranty} 1280w`} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 640px" width={1280} height={832} decoding="async"
              src={homeWarranty}
              alt="Phoenix plumber repairing a leaking copper pipe under a kitchen sink"
              className="w-full h-auto block"
              loading="lazy"
            />
          </div>
          <h2 className="mt-6 text-3xl md:text-4xl font-extrabold text-secondary">
            Three generations of Phoenix plumbers — under one roof.
          </h2>
          <p className="mt-4 text-foreground leading-relaxed">
            Phoenix Plumbing Co. has been family-owned since {BUSINESS.founded}. Licensed plumbers
            lead our plumbing work; apprentices and helpers work under licensed supervision when
            needed. We hire slowly, train constantly, and back every job with a written workmanship
            warranty because our name is on the truck.
          </p>
          <p className="mt-4 text-foreground leading-relaxed">
            That's why nearly 70% of our calls come from repeat customers and word-of-mouth referrals across
            Scottsdale, Mesa, Chandler, Gilbert, and the rest of the Valley.
          </p>
          <PhoneButton size="lg" className="mt-6" />
        </div>
      </section>

      <CTABand />

      {/* Phoenix-specific education */}
      <section className="mx-auto max-w-7xl px-4 py-14">
        <div className="max-w-3xl">
          <p className="text-accent-ink font-bold text-sm uppercase tracking-widest">Built for Phoenix</p>
          <h2 className="mt-2 text-3xl md:text-4xl font-extrabold text-secondary">
            Why Phoenix plumbing is different
          </h2>
          <p className="mt-4 text-lg text-foreground leading-relaxed">
            Phoenix isn't a generic plumbing market. Our hard water, slab-on-grade construction, monsoon
            pressure swings, and 115°F attic temps put unique strain on residential plumbing systems. Knowing
            the local failure modes is the difference between a $200 fix and a $5,000 emergency.
          </p>
        </div>
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {[
            { Icon: Droplets, t: "Hard Water (16–20 gpg)", d: "Phoenix tap water is among the hardest in the U.S. Scale shortens water heater life to 8–12 years and clogs fixtures. A whole-home softener pays for itself." },
            { Icon: Thermometer, t: "Slab-on-Grade Homes", d: "Most Valley homes have copper or PEX pipes running under the concrete slab. A pinhole leak can waste 10,000+ gallons a month before you see a stain." },
            { Icon: AlertTriangle, t: "Polybutylene Pipes", d: "Homes built 1978–1995 often have polybutylene supply lines that fail without warning. If you have them, plan the repipe before it leaks." },
            { Icon: Truck, t: "Monsoon Sewer Backups", d: "July–September storms overwhelm older sewer mains and root-cracked lines. A camera inspection in spring catches it before the rain does." },
          ].map(({ Icon, t, d }) => (
            <div key={t} className="rounded-xl border border-border bg-card p-6">
              <Icon className="h-8 w-8 text-primary" />
              <div className="mt-3 font-extrabold text-secondary">{t}</div>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Service Van */}
      <section className="bg-secondary text-secondary-foreground">
        <div className="mx-auto max-w-7xl px-4 py-14 grid gap-10 lg:grid-cols-2 items-center">
          <div className="rounded-2xl overflow-hidden shadow-2xl ring-1 ring-secondary-foreground/10">
            <img srcSet={`${homeFleet640} 640w, ${homeFleet1024} 1024w, ${homeFleet} 1264w`} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 640px" width={1264} height={848} decoding="async"
              src={homeFleet}
              alt="Phoenix Plumbing Co. service van in a Phoenix neighborhood"
              className="w-full h-auto block"
              loading="lazy"
            />
          </div>
          <div>
            <p className="text-accent font-bold text-sm uppercase tracking-widest">Fully-stocked trucks</p>
            <h2 className="mt-2 text-3xl md:text-4xl font-extrabold tracking-tight">
              Look for our van in your neighborhood.
            </h2>
            <p className="mt-4 text-secondary-foreground/85 text-lg">
              Every truck rolls with the parts and tools to finish 90% of jobs on the first visit — no return trips, no surprise delays.
            </p>
            <PhoneButton size="lg" className="mt-6" />
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="mx-auto max-w-7xl px-4 py-14 grid gap-10 lg:grid-cols-[1.2fr_1fr] items-center">
        <div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-secondary">How it works</h2>
          <p className="mt-3 text-foreground max-w-xl">
            The same straightforward process for every Phoenix homeowner — whether it's a 2 AM burst pipe or a
            scheduled water heater swap. No mystery, no pressure, no fine print.
          </p>
          <ol className="mt-8 grid gap-5 md:grid-cols-2">
            {[
              { n: "01", t: "Call live", d: "A real Phoenix dispatcher answers — not a bot, not an answering service. We triage your issue and set an arrival window on the call." },
              { n: "02", t: "Same-day dispatch", d: "A licensed, background-checked plumber rolls up in a fully stocked truck. We text when we're 20 minutes out so you're never waiting blind." },
              { n: "03", t: "Upfront flat quote", d: "Full diagnosis first, then a flat all-in price before any work starts. You approve in writing — no surprise add-ons at the end." },
              { n: "04", t: "Fix, clean, guarantee", d: "We complete the job, walk you through what we did, clean up, and back it with a written workmanship warranty." },
            ].map((s) => (
              <li key={s.n} className="rounded-xl bg-card border border-border p-5">
                <div className="text-3xl font-black text-primary">{s.n}</div>
                <div className="mt-1 font-bold text-secondary">{s.t}</div>
                <p className="mt-1 text-sm text-muted-foreground">{s.d}</p>
              </li>
            ))}
          </ol>
        </div>
        <div className="rounded-2xl overflow-hidden shadow-xl ring-1 ring-border">
          <img srcSet={`${homeProcess640} 640w, ${homeProcess1024} 1024w, ${homeProcess} 1280w`} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 640px" width={1280} height={832} decoding="async"
            src={homeProcess}
            alt="Licensed Phoenix plumber installing a new toilet in a home bathroom"
            className="w-full h-auto block"
            loading="lazy"
          />
        </div>
      </section>

      {/* Areas */}
      <section className="bg-muted/40 border-y border-border">
        <div className="mx-auto max-w-7xl px-4 py-14 grid gap-10 lg:grid-cols-[1fr_1.2fr] items-center">
          <div className="rounded-2xl overflow-hidden shadow-xl ring-1 ring-border">
            <img srcSet={`${homeMap640} 640w, ${homeMap1024} 1024w, ${homeMap} 1280w`} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 640px" width={1280} height={832} decoding="async"
              src={homeMap}
              alt="Phoenix Plumbing Co. service van serving the Valley"
              className="w-full h-auto block"
              loading="lazy"
            />
          </div>
          <div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-secondary">Areas We Serve</h2>
            <p className="mt-2 text-muted-foreground">Click your city for local plumbing service info.</p>
            <div className="mt-6 grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {CITIES.map((c) => (
                <Link
                  key={c.slug}
                  to="/areas-served/$city"
                  params={{ city: c.slug }}
                  className="rounded-lg border border-border bg-card px-4 py-3 font-bold text-secondary hover:border-primary flex items-center gap-2"
                >
                  <MapPin className="h-4 w-4 text-primary flex-shrink-0" />
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="mx-auto max-w-7xl px-4 py-14">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr] items-center">
          <div className="rounded-2xl overflow-hidden shadow-xl ring-1 ring-border">
            <img srcSet={`${homeReviews640} 640w, ${homeReviews1024} 1024w, ${homeReviews} 1280w`} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 640px" width={1280} height={832} decoding="async"
              src={homeReviews}
              alt="Phoenix Plumbing Co. plumber on a Phoenix, AZ service call"
              className="w-full h-auto block"
              loading="lazy"
            />
          </div>
          <div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-secondary">
              Phoenix Plumbing Co. Reviews
            </h2>
            <p className="mt-4 text-foreground">
              Customer feedback helps new customers understand what to expect when they call our team.
              Read real reviews on our Google Business Profile.
            </p>
            <div className="mt-6"><Testimonials /></div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-muted/40 border-y border-border">
        <div className="mx-auto max-w-4xl px-4 py-14">
          <h2 className="text-3xl md:text-4xl font-extrabold text-secondary mb-6">Frequently Asked Questions</h2>
          <FAQ items={HOME_FAQS} />
        </div>
      </section>

      {/* Blog */}
      <section className="mx-auto max-w-7xl px-4 py-14">
        <h2 className="text-3xl md:text-4xl font-extrabold text-secondary">Plumbing tips from our shop</h2>
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {POSTS.slice(0, 3).map((p) => (
            <Link key={p.slug} to={`/${p.slug}` as any} className="group rounded-xl border border-border bg-card overflow-hidden hover:border-primary">
              <div className="h-32 bg-gradient-to-br from-primary/10 via-accent/10 to-secondary/10 flex items-center justify-center border-b border-border">
                <Wrench className="h-10 w-10 text-primary" />
              </div>
              <div className="p-5">
                <div className="text-xs font-bold text-primary uppercase tracking-widest">{new Date(p.date).toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" })}</div>
                <h3 className="mt-1 font-extrabold text-secondary">{p.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{p.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Why homeowners choose us — keyword-rich anchor block */}
      <section className="bg-muted/40 border-t border-border">
        <div className="mx-auto max-w-4xl px-4 py-14">
          <h2 className="text-3xl md:text-4xl font-extrabold text-secondary">
            Why Homeowners Choose Our Phoenix Plumbing Company
          </h2>
          <p className="mt-5 text-lg text-foreground leading-relaxed">
            Phoenix Plumbing Co. is a family-owned Phoenix plumbing company operating from
            North Phoenix (85028). We built the shop to be the kind of local plumbing
            company we would want to hire: real people answer the phone, a licensed plumber
            explains the fix in plain English, and every job carries a written workmanship
            warranty on the invoice.
          </p>
          <p className="mt-4 text-foreground leading-relaxed">
            Our team of phoenix plumbing specialists lives and works across the Valley, so
            the plumber knocking on your door already knows your neighborhood's water
            pressure, soil, and pipe history. Whether you need residential plumbers for a
            leaky faucet, licensed plumbing contractors for a permitted remodel, or a
            reliable plumbing and drain service for a stubborn kitchen sink, we run the
            same trucks, the same warranty, and the same flat-rate pricing on every call.
          </p>
          <p className="mt-4 text-foreground leading-relaxed">
            Start with our{" "}
            <Link to="/drain-cleaning-and-clog-removal" className="text-primary font-semibold underline">plumbing and drain service</Link>,{" "}
            hire our{" "}
            <Link to="/phoenix-plumbing-contractors" className="text-primary font-semibold underline">plumbing contractors</Link>{" "}
            for a permitted install, book our{" "}
            <Link to="/residential-plumbing-phoenix" className="text-primary font-semibold underline">residential plumbers</Link>{" "}
            for a repair, or explore the full list of{" "}
            <a href="#services" className="text-primary font-semibold underline">phoenix plumbing specialists</a>{" "}
            services we offer across the Valley.
          </p>
        </div>
      </section>
    </>
  );
}
